// implement the precheck test screen

import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ExternalSessionService } from 'src/app/services/external-session.service';

@Component({
    selector: 'app-precheck-test',
    templateUrl: './precheck-test.component.html',
    styleUrls: ['./precheck-test.component.scss']
})
export class PrecheckTestComponent implements OnInit {
    sessionForm: FormGroup;
    sessionId: string | null = null;
    iframeUrl: SafeResourceUrl | null = null;
    isLoading = false;
    sessionDetailsExpanded = false;
    sessionDetails: any = null;

    constructor(
        private fb: FormBuilder,
        private externalSessionService: ExternalSessionService,
        private spinner: NgxSpinnerService,
        private toastr: ToastrService,
        private sanitizer: DomSanitizer
    ) { }

    ngOnInit(): void {
        this.initForm();
    }

    private initForm(): void {
        this.sessionForm = this.fb.group({
            userId: ['', [Validators.required]],
            marketplaceId: ['', [Validators.required]]
        });
    }

    createSession(): void {
        if (this.sessionForm.invalid) {
            this.toastr.error('Please fill all required fields');
            this.sessionForm.markAllAsTouched();
            return;
        }

        this.isLoading = true;
        this.spinner.show();

        const { userId, marketplaceId } = this.sessionForm.value;

        // Create external session with isReport = false for precheck
        this.externalSessionService.createExternalSession(
            userId,
            3600, // 1 hour expiration
            false, // Not infinite
            false,  // Not for report (for precheck)
            null,   // No submissionId for precheck
            marketplaceId // Add marketplaceId for institution mapping
        ).subscribe(
            (response: string) => {
                // Extract session ID from the URL response
                const sessionId = response.split('precheck-external/session/')[1];
                this.sessionId = sessionId;

                this.createIframeUrl();

                this.spinner.hide();
                this.isLoading = false;
                this.toastr.success('Session created successfully');
            },
            (error) => {
                console.error('Error creating session:', error);
                this.spinner.hide();
                this.isLoading = false;
                this.toastr.error(error.error?.message || 'Failed to create session');
            }
        );
    }

    /**
     * Toggles the expanded state of the session details
     */
    toggleSessionDetails(): void {
        this.sessionDetailsExpanded = !this.sessionDetailsExpanded;

        // If expanding and we haven't loaded session details yet
        if (this.sessionDetailsExpanded && this.sessionId && !this.sessionDetails) {
            this.loadSessionDetails();
        }
    }

    /**
     * Loads the session details from the API
     */
    loadSessionDetails(): void {
        if (!this.sessionId) return;

        this.isLoading = true;

        this.externalSessionService.verifySession(this.sessionId, true)
            .subscribe(
                (response) => {
                    this.sessionDetails = response;
                    this.isLoading = false;
                },
                (error) => {
                    console.error('Error loading session details:', error);
                    this.isLoading = false;
                    this.toastr.error('Failed to load session details');
                }
            );
    }

    /**
     * Copies the session ID to clipboard
     */
    copySessionId(event?: MouseEvent): void {
        // Prevent event from propagating to parent (toggle)
        if (event) {
            event.stopPropagation();
        }

        if (this.sessionId) {
            navigator.clipboard.writeText(this.sessionId)
                .then(() => {
                    this.toastr.success('Session ID copied to clipboard');
                })
                .catch(err => {
                    console.error('Error copying session ID:', err);
                    this.toastr.error('Failed to copy session ID');
                });
        }
    }

    private createIframeUrl(): void {
        if (!this.sessionId) {
            this.iframeUrl = null;
            return;
        }

        // Create a URL with the session ID
        const baseUrl = '/precheck-external';
        const url = `${baseUrl}/session/${encodeURIComponent(this.sessionId)}`;
        this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }

    clearSession(): void {
        this.sessionId = null;
        this.iframeUrl = null;
        this.sessionDetails = null;
        this.sessionDetailsExpanded = false;
    }
}
